// src/components/WhatsAppFloatButton.tsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Button } from 'antd';
import { WhatsAppOutlined, CloseOutlined } from '@ant-design/icons';
import gsap from 'gsap';
import { WhatsAppNumberEntry } from '@eshop/components/floating-buttons/normalizers.ts';

export interface WhatsAppFloatButtonProps {
  wspNumbers: WhatsAppNumberEntry[];
  floatBtnMsjTxt?: string | null;
  separationDeg?: number;
  referenceDeg?: number;
  distance?: number;
  style?: React.CSSProperties;
  className?: string;
  onExpansionChange?: (isExpanded: boolean) => void;
}

export const WhatsAppFloatButton: React.FC<WhatsAppFloatButtonProps> = ({
                                                                          wspNumbers,
                                                                          floatBtnMsjTxt='',
                                                                          separationDeg = 40,
                                                                          referenceDeg = -55,
                                                                          distance = 100,
                                                                          style,
                                                                          className,
                                                                          onExpansionChange,
                                                                        }) => {
  const numbers = useMemo(() => Object.entries(wspNumbers), [wspNumbers]);
  const [open, setOpen] = useState(false);
  const [showMessage, setShowMessage] = useState(!!floatBtnMsjTxt);
  const messageRef = useRef<HTMLDivElement>(null);

  // Notificar cambio de expansión
  useEffect(() => {
    onExpansionChange?.(open);
  }, [open, onExpansionChange]);

  // Animación del globo de mensaje al cargar
  useEffect(() => {
    if (floatBtnMsjTxt && messageRef.current) {
      // Inicializar con estado invisible
      gsap.set(messageRef.current, {
        opacity: 0,
        scale: 0,
        y: 20,
        rotation: -5
      });

      // Animación de entrada con delay
      const tl = gsap.timeline({ delay: 1.5 });
      tl.to(messageRef.current, {
        duration: 0.6,
        opacity: 1,
        scale: 1,
        y: 0,
        rotation: 0,
        ease: "back.out(1.7)"
      })
        .to(messageRef.current, {
          duration: 0.3,
          y: -3,
          ease: "power2.out",
          yoyo: true,
          repeat: 1
        }, "-=0.2");
    }
  }, [floatBtnMsjTxt]);

  const closeMessage = () => {
    if (messageRef.current) {
      gsap.to(messageRef.current, {
        duration: 0.4,
        opacity: 0,
        scale: 0.8,
        y: 10,
        ease: "power2.in",
        onComplete: () => setShowMessage(false)
      });
    }
  };

  if (numbers.length === 0) return null;

  const openWhatsapp = (num: string) => {
    const url = `https://wa.me/${num.replace(/\D/g, '')}?text=${encodeURIComponent(
      `Hola! Vi esto en la web\n\n${window.location.href}`,
    )}`;
    window.open(url, '_blank');
  };

  const toggleSubs = () => {
    const newOpenState = !open;
    setOpen(newOpenState);

    const btns = document.querySelectorAll('.wsp-sub-btn');
    const n = btns.length;
    const span = separationDeg * (n - 1);
    const startDeg = referenceDeg - span / 2;

    btns.forEach((el, i) => {
      const angleDeg = startDeg + separationDeg * i;
      const angleRad = ((-90 + angleDeg) * Math.PI) / 180;
      const x = newOpenState ? Math.cos(angleRad) * distance : 0;
      const y = newOpenState ? Math.sin(angleRad) * distance : 0;

      gsap.to(el as HTMLElement, {
        duration: 0.4,
        x,
        y,
        scale: newOpenState ? 1 : 0.8,
        opacity: newOpenState ? 1 : 0,
        pointerEvents: newOpenState ? 'auto' : 'none',
        ease: "back.out(1.7)",
        delay: newOpenState ? i * 0.1 : 0,
      });
    });
  };

  return (
    <div
      style={{ position: 'relative', ...style }}
      className={`whatsapp-float-container ${open ? 'open' : ''} ${className || ''}`}
    >
      {/* Globo de mensaje */}
      {showMessage && !open && floatBtnMsjTxt && (
        <div
          ref={messageRef}
          className="whatsapp-message-bubble"
        >
          <div className="message-content">
            <span className="message-text">{floatBtnMsjTxt}</span>
            <button
              className="message-close-btn"
              onClick={closeMessage}
              aria-label="Cerrar mensaje"
            >
              ×
            </button>
          </div>
          <div className="message-tail"></div>
        </div>
      )}

      {numbers.length === 1 ? (
        <Button
          type="text"
          shape="circle"
          icon={<WhatsAppOutlined className='whatsapp-logo' />}
          title={numbers[0][1].label}
          className="whatsapp-main-btn single-btn w_050pxi h_050px border shadow bg-white d-flex justify-content-center align-items-center"
          onClick={() => openWhatsapp(numbers[0][1].phone)}
        />
      ) : (
        <>
          <Button
            type="text"
            shape="circle"
            icon={open ? <CloseOutlined /> : <WhatsAppOutlined className='whatsapp-logo'/>}
            className="whatsapp-main-btn w_050pxi h_050px border shadow d-flex justify-content-center align-items-center"
            onClick={toggleSubs}
          />
          {numbers.map(([label, num]) => (
            <Button
              key={label}
              className="wsp-sub-btn"
              onClick={() => {
                toggleSubs();
                setTimeout(() => openWhatsapp(num.phone), 100);
              }}
            >
              <span className="wsp-sub-btn-label">{num.label}</span>
            </Button>
          ))}
        </>
      )}
    </div>
  );
};

export default WhatsAppFloatButton;
